import { lazy, Suspense, useEffect } from "react";
import Box from "@mui/material/Box";
import { Navigate, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import AppLayout from "../../layouts/app/AppLayout";
import MinimalLayout from "../../layouts/minimal/MinimalLayout";
import PublicLayout from "../../layouts/public/PublicLayout";
import LoginPage from "../../features/auth/pages/LoginPage";
import CompleteSignupPage from "../../features/auth/pages/CompleteSignupPage";
import SelectAccountPage from "../../features/auth/pages/SelectAccountPage";
import RootRedirect from "./RootRedirect";
import ProtectedRoute from "./guards/ProtectedRoute";
import EmailVerifiedRoute from "./guards/EmailVerifiedRoute";
import PublicAuthRoute from "./guards/PublicAuthRoute";
import RouteFallback from "./fallback/RouteFallback";
import { clearLogoutSessionCache } from "../auth/authSessionState";
import { useAuth } from "../providers/AuthProvider";
import { campaignRoutePaths } from "../../features/campaigns/config/campaignRoutes";
import use123zapStore from "../../stores/123zapStore";
import {
  areFeaturesAllowed,
  canAccessPermission,
  hasKnownPermissions,
  isFeatureAllowed,
} from "../../shared/lib/access/featureAccess";

const ChannelPageList = lazy(() => import(/* webpackPrefetch: true */ "../../features/channels/pages/ChannelsOverviewPage"));
const ChannelPageOptions = lazy(() => import("../../features/channels/pages/ChannelAddPage"));
const CreateIntegrationAlcancePage = lazy(() => import("../../features/channels/pages/providers/sms-alcance/SmsAlcanceIntegrationPage"));
const CreateIntegrationSesPage = lazy(() => import("../../features/channels/pages/providers/email-ses/EmailSesIntegrationPage"));
const CreateIntegrationEmbeddedPage = lazy(() => import("../../features/channels/pages/providers/whatsapp-api/WhatsAppApiEmbeddedSignupPage"));
const ChatbotBuilderPage = lazy(() => import("../../features/chatbots/pages/ChatbotBuilderPage"));

const TemplatesPageList = lazy(() => import(/* webpackPrefetch: true */ "../../features/templates/pages/TemplatesOverviewPage"));
const TemplatePageAdd = lazy(() => import("../../features/templates/options"));
const TemplatePageChannelAdd = lazy(() => import("../../features/templates/create"));

const AccountIntegrationsPage = lazy(() => import(/* webpackPrefetch: true */ "../../features/settings/pages/AccountIntegrationsPage"));
const AccountFeaturesPage = lazy(() => import("../../features/settings/pages/AccountFeaturesPage"));

const PasswordRecoveryPage = lazy(() => import("../../features/auth/pages/ForgotPasswordPage"));
const CreatePasswordPage = lazy(() => import("../../features/auth/pages/CreatePasswordPage"));
const DashboardPage = lazy(() => import(/* webpackPrefetch: true */ "../../features/dashboard"));
const AccountSettingsPage = lazy(() => import(/* webpackPrefetch: true */ "../../features/settings"));
const AccountUsersPage = lazy(() => import("../../features/settings/pages/AccountUsersPage"));
const ContactsPage = lazy(() => import(/* webpackPrefetch: true */ "../../features/contacts"));
const AttendancePage = lazy(() => import("../../features/attendance"));

const CampaignCreatePage = lazy(() => import("../../features/campaigns/pages/CampaignCreatePage"));
const CampaignEditPage = lazy(() => import("../../features/campaigns/pages/CampaignEditPage"));
const CampaignAnalysisPage = lazy(() => import("../../features/campaigns/pages/CampaignAnalysisPage"));
const CampaignPage = lazy(() => import(/* webpackPrefetch: true */ "../../features/campaigns"));

const Sheets = lazy(() => import(/* webpackPrefetch: true */ "../../features/data-sources/overview"));
const AddSheetsOptions = lazy(() => import("../../features/data-sources/options"));
const PageAddGoogleSheets = lazy(() => import("../../features/data-sources/types/GoogleSheets/create/addGoogleSheets"));
const PageAddExcel = lazy(() => import("../../features/data-sources/types/Excel/create/addExcel"));
const PageAddApiRest = lazy(() => import("../../features/data-sources/types/ApiRest/create/addApiRest"));
const AddSheetsIntegrationInvoice = lazy(() => import("../../features/data-sources/types/Erps/create/addSheetsIntegrationInvoice"));

const SignUp = lazy(() => import("../../features/auth/pages/SignupPage"));
const VerifyEmailPage = lazy(() => import("../../features/auth/pages/VerifyEmailPage"));

const NotFound = lazy(() => import("../../shared/components/patterns/NotFound"));
const SessionExpired = lazy(() => import("../../shared/components/patterns/SessionExpired"));
const DevPlayground = lazy(() => import("../dev/DevPlayground"));

const SupportAccessPage = lazy(() => import("../../features/auth/pages/SupportAccessPage"));
const ResetRequiredPasswordPage = lazy(() => import("../../features/auth/pages/ResetPasswordPage"));
const WhatsappTestePage = lazy(() => import("../../features/channels/pages/WhatsAppTestPage"));
const AccountAccessTransitionPreview = lazy(() =>
  import("../../features/auth/pages/SelectAccountPage").then((module) => ({
    default: module.AccountAccessTransitionPreview,
  }))
);

const appRoutePrefixes = [
  "/painel",
  "/canais",
  "/modelos",
  "/listagens",
  "/contatos",
  "/atendimentos",
  "/campanhas",
  "/integracoes",
  "/ajustes",
  "/configuracoes",
];

function getRouteShellKey(pathname) {
  if (
    appRoutePrefixes.some(
      (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
    )
  ) {
    return "app-shell";
  }

  if (
    pathname === "/verificar-email" ||
    pathname === "/finalizar-cadastro" ||
    pathname === "/selecionar-conta"
  ) {
    return "auth-minimal-shell";
  }

  return "public-shell";
}

function LogoutSessionExpiredRoute() {
  const navigate = useNavigate();

  const handleLoginRedirect = () => {
    clearLogoutSessionCache();
    localStorage.clear();
    sessionStorage.clear();
    navigate("/", { replace: true });
  };

  return <SessionExpired onAction={handleLoginRedirect} />;
}

function useFeatureAccessContext() {
  const { user } = useAuth();
  const featureAccess = use123zapStore((state) => state.featureAccess);
  const featureAccessAccountId = use123zapStore((state) => state.featureAccessAccountId);
  const featureAccessLoaded = use123zapStore((state) => state.featureAccessLoaded);
  const featureAccessLoading = use123zapStore((state) => state.featureAccessLoading);
  const hydrateFeatureAccessFromCache = use123zapStore(
    (state) => state.hydrateFeatureAccessFromCache
  );
  const loadPlanoAtual = use123zapStore((state) => state.loadPlanoAtual);
  const currentAccountId = user?.accountId || "";
  const featureAccessHasAccountMismatch =
    Boolean(featureAccess && currentAccountId && featureAccessAccountId !== currentAccountId);

  useEffect(() => {
    if (!currentAccountId || featureAccessLoading) return;

    if (!featureAccessLoaded || featureAccessHasAccountMismatch) {
      const hasCachedAccess = hydrateFeatureAccessFromCache(currentAccountId);

      if (hasCachedAccess) {
        loadPlanoAtual(true, currentAccountId, { silent: true });
        return;
      }

      loadPlanoAtual(true, currentAccountId);
    }
  }, [
    currentAccountId,
    featureAccessHasAccountMismatch,
    featureAccessLoaded,
    featureAccessLoading,
    hydrateFeatureAccessFromCache,
    loadPlanoAtual,
  ]);

  return {
    featureAccess,
    loading: Boolean(
      currentAccountId &&
      (featureAccessLoading || !featureAccessLoaded || featureAccessHasAccountMismatch)
    ),
    user,
  };
}

function FeatureAccessRoute({ anyCapabilities, children, capabilities, module, permission, permissions }) {
  const { featureAccess, loading, user } = useFeatureAccessContext();

  if (loading) {
    return <RouteFallback />;
  }

  if (module && !isFeatureAllowed(featureAccess, module)) {
    return <NotFound />;
  }

  if (capabilities && !areFeaturesAllowed(featureAccess, capabilities)) {
    return <NotFound />;
  }

  if (anyCapabilities && !isFeatureAllowed(featureAccess, anyCapabilities)) {
    return <NotFound />;
  }

  const requiredPermissions = [
    ...(permission ? [permission] : []),
    ...(Array.isArray(permissions) ? permissions : []),
  ];

  if (requiredPermissions.length > 0) {
    const denied = requiredPermissions.some((requiredPermission) => {
      const permissionModule = requiredPermission.module || module;
      return (
        !hasKnownPermissions(user, permissionModule) ||
        !canAccessPermission(user, permissionModule, requiredPermission.action)
      );
    });
    if (denied) {
      return <NotFound />;
    }
  }

  return children;
}

function SettingsDefaultRoute() {
  const { featureAccess, loading, user } = useFeatureAccessContext();

  if (loading) {
    return <RouteFallback />;
  }

  const candidates = [
    { module: "account_settings", permission: { action: "use", module: "account_settings" }, to: "/ajustes/empresa" },
    { module: "account_users", permission: { action: "use", module: "account_users" }, to: "/ajustes/usuarios" },
    { module: "integrations", permission: { action: "use", module: "integrations" }, to: "/ajustes/integracoes" },
    { module: "account_features", permission: { action: "use", module: "account_features" }, to: "/ajustes/funcionalidades" },
  ];

  const target = candidates.find((candidate) => {
    const permissionModule = candidate.permission.module;
    return (
      isFeatureAllowed(featureAccess, candidate.module) &&
      hasKnownPermissions(user, permissionModule) &&
      canAccessPermission(user, permissionModule, candidate.permission.action)
    );
  });

  if (!target) {
    return <NotFound />;
  }

  return <Navigate to={target.to} replace />;
}

function DashboardEntryRoute() {
  const { loading } = useFeatureAccessContext();

  if (loading) {
    return <DashboardPage />;
  }

  return <DashboardPage />;
}

function LegacySettingsRedirect({ to }) {
  return (
    <FeatureAccessRoute module="account_settings" permission={{ action: "use", module: "account_settings" }}>
      <Navigate to={to} replace />
    </FeatureAccessRoute>
  );
}

function LegacyUsersRedirect() {
  return (
    <FeatureAccessRoute module="account_users" permission={{ action: "use", module: "account_users" }}>
      <Navigate to="/ajustes/usuarios" replace />
    </FeatureAccessRoute>
  );
}

function LegacyIntegrationsRedirect() {
  return (
    <FeatureAccessRoute module="integrations" permission={{ action: "use", module: "integrations" }}>
      <Navigate to="/ajustes/integracoes" replace />
    </FeatureAccessRoute>
  );
}

/*
 * Legacy redirects are guarded too. Otherwise an old URL could reveal a
 * protected Ajustes destination even when the topbar hides it.
 */

function SettingsRoute({ children, module, action = "use" }) {
  return (
    <FeatureAccessRoute module={module} permission={{ action, module }}>
      {children}
    </FeatureAccessRoute>
  );
}

function ChannelRoute({ children, capabilities, action = "use" }) {
  return (
    <FeatureAccessRoute capabilities={capabilities} module="channels" permission={{ action, module: "channels" }}>
      {children}
    </FeatureAccessRoute>
  );
}

function ErpAudienceRoute({ children }) {
  return (
    <FeatureAccessRoute
      capabilities={["integrations.erp.manage"]}
      module="audiences"
      permissions={[
        { action: "manage", module: "audiences" },
        { action: "use", module: "integrations" },
      ]}
    >
      {children}
    </FeatureAccessRoute>
  );
}

export default function AppRoutes() {
  const location = useLocation();
  const routeShellKey = getRouteShellKey(location.pathname);

  return (
    <Suspense fallback={<RouteFallback />}>
      <Box
        key={routeShellKey}
        sx={{
          animation: "routeShellIn 120ms ease-out both",
          minWidth: 0,
          width: "100%",
          "@keyframes routeShellIn": {
            from: {
              opacity: 0.92,
            },
            to: {
              opacity: 1,
            },
          },
          "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
          },
        }}
      >
        <Routes>
          {process.env.NODE_ENV !== "production" && (
            <Route path="/_dev" element={<DevPlayground />} />
          )}

          <Route element={<PublicLayout />}>
            <Route path="/" element={<RootRedirect />} />
            <Route element={<PublicAuthRoute />}>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/esqueci-senha" element={<PasswordRecoveryPage />} />
              <Route path="/criar-senha" element={<CreatePasswordPage />} />
              <Route path="/cadastro" element={<SignUp />} />
              <Route path="/support-login" element={<SupportAccessPage />} />
              <Route path="/reset-senha" element={<ResetRequiredPasswordPage />} />
            </Route>
            <Route path="/whatsapp_teste" element={<WhatsappTestePage />} />
            <Route path="/preview/acesso-carregando" element={<AccountAccessTransitionPreview />} />
            <Route path="/logout" element={<LogoutSessionExpiredRoute />} />
          </Route>

          <Route element={<ProtectedRoute />}>
            <Route element={<MinimalLayout />}>
              <Route path="/verificar-email" element={<VerifyEmailPage />} />
              <Route path="/selecionar-conta" element={<SelectAccountPage />} />
            </Route>

            <Route element={<EmailVerifiedRoute />}>
              <Route element={<MinimalLayout />}>
                <Route path="/finalizar-cadastro" element={<CompleteSignupPage />} />
              </Route>

              <Route element={<AppLayout />}>
                <Route path="/painel" element={<DashboardEntryRoute />} />
                <Route path="/ajustes" element={<SettingsDefaultRoute />} />
                <Route
                  path="/ajustes/empresa"
                  element={(
                    <SettingsRoute module="account_settings">
                      <AccountSettingsPage />
                    </SettingsRoute>
                  )}
                />
                <Route
                  path="/ajustes/empresa/limites-de-uso"
                  element={(
                    <SettingsRoute module="account_settings">
                      <AccountSettingsPage />
                    </SettingsRoute>
                  )}
                />
                <Route
                  path="/ajustes/integracoes"
                  element={(
                    <SettingsRoute module="integrations">
                      <AccountIntegrationsPage />
                    </SettingsRoute>
                  )}
                />
                <Route
                  path="/ajustes/funcionalidades"
                  element={(
                    <SettingsRoute module="account_features">
                      <AccountFeaturesPage />
                    </SettingsRoute>
                  )}
                />
                <Route path="/ajustes/conta" element={<LegacySettingsRedirect to="/ajustes/empresa" />} />
                <Route path="/ajustes/conta/limites-de-uso" element={<LegacySettingsRedirect to="/ajustes/empresa/limites-de-uso" />} />
                <Route
                  path="/ajustes/usuarios/novo/editar"
                  element={(
                    <SettingsRoute action="manage" module="account_users">
                      <AccountUsersPage />
                    </SettingsRoute>
                  )}
                />
                <Route
                  path="/ajustes/usuarios/*"
                  element={(
                    <SettingsRoute module="account_users">
                      <AccountUsersPage />
                    </SettingsRoute>
                  )}
                />
                <Route path="/configuracoes/usuarios" element={<LegacyUsersRedirect />} />
                <Route path="/configuracoes/usuarios/*" element={<LegacyUsersRedirect />} />
                <Route path="/configuracoes" element={<LegacySettingsRedirect to="/ajustes/empresa" />} />
                <Route path="/configuracoes/informacoes-da-conta" element={<LegacySettingsRedirect to="/ajustes/empresa" />} />
                <Route path="/configuracoes/informacoes-da-conta/*" element={<LegacySettingsRedirect to="/ajustes/empresa" />} />
                <Route path="/configuracoes/cotas" element={<LegacySettingsRedirect to="/ajustes/empresa/limites-de-uso" />} />
                <Route path="/configuracoes/cotas/*" element={<LegacySettingsRedirect to="/ajustes/empresa/limites-de-uso" />} />
                <Route path="/configuracoes/seguranca" element={<LegacySettingsRedirect to="/ajustes/empresa" />} />
                <Route path="/configuracoes/seguranca/*" element={<LegacySettingsRedirect to="/ajustes/empresa" />} />
                <Route path="/configuracoes/*" element={<LegacySettingsRedirect to="/ajustes/empresa" />} />

                <Route
                  path="/canais"
                  element={(
                    <ChannelRoute>
                      <ChannelPageList />
                    </ChannelRoute>
                  )}
                />
                <Route
                  path="/canais/adicionar"
                  element={(
                    <ChannelRoute action="manage">
                      <ChannelPageOptions />
                    </ChannelRoute>
                  )}
                />
                <Route
                  path="/canais/adicionar/whatsapp-api"
                  element={(
                    <ChannelRoute action="manage" capabilities={["channels.whatsapp_api.use"]}>
                      <CreateIntegrationEmbeddedPage />
                    </ChannelRoute>
                  )}
                />
                <Route
                  path="/canais/adicionar/sms"
                  element={(
                    <ChannelRoute action="manage" capabilities={["channels.sms.use"]}>
                      <CreateIntegrationAlcancePage />
                    </ChannelRoute>
                  )}
                />
                <Route
                  path="/canais/adicionar/email"
                  element={(
                    <ChannelRoute action="manage" capabilities={["channels.email.use"]}>
                      <CreateIntegrationSesPage />
                    </ChannelRoute>
                  )}
                />
                <Route
                  path="/canais/chatbot"
                  element={(
                    <ChannelRoute action="manage">
                      <ChatbotBuilderPage />
                    </ChannelRoute>
                  )}
                />
                <Route path="/canais/configurar" element={<Navigate to="/canais/chatbot" replace />} />

                <Route
                  path="/modelos"
                  element={(
                    <FeatureAccessRoute module="message_templates" permission={{ action: "use", module: "message_templates" }}>
                      <TemplatesPageList />
                    </FeatureAccessRoute>
                  )}
                />
                <Route
                  path="/modelos/canal"
                  element={(
                    <FeatureAccessRoute module="message_templates" permission={{ action: "manage", module: "message_templates" }}>
                      <TemplatePageAdd />
                    </FeatureAccessRoute>
                  )}
                />
                <Route
                  path="/modelos/canal/whatsapp-api"
                  element={(
                    <FeatureAccessRoute capabilities={["message_templates.channel.whatsapp_api.use"]} module="message_templates" permission={{ action: "manage", module: "message_templates" }}>
                      <TemplatePageChannelAdd channel="whatsapp_api" />
                    </FeatureAccessRoute>
                  )}
                />
                <Route
                  path="/modelos/canal/email"
                  element={(
                    <FeatureAccessRoute capabilities={["message_templates.channel.email.use"]} module="message_templates" permission={{ action: "manage", module: "message_templates" }}>
                      <TemplatePageChannelAdd channel="email" />
                    </FeatureAccessRoute>
                  )}
                />
                <Route
                  path="/modelos/canal/sms"
                  element={(
                    <FeatureAccessRoute capabilities={["message_templates.channel.sms.use"]} module="message_templates" permission={{ action: "manage", module: "message_templates" }}>
                      <TemplatePageChannelAdd channel="sms" />
                    </FeatureAccessRoute>
                  )}
                />

                <Route
                  path="/listagens"
                  element={(
                    <FeatureAccessRoute module="audiences" permission={{ action: "use", module: "audiences" }}>
                      <Sheets />
                    </FeatureAccessRoute>
                  )}
                />
                <Route
                  path="/listagens/adicionar"
                  element={(
                    <FeatureAccessRoute module="audiences" permission={{ action: "manage", module: "audiences" }}>
                      <AddSheetsOptions />
                    </FeatureAccessRoute>
                  )}
                />
                <Route
                  path="/listagens/adicionar/google"
                  element={(
                    <FeatureAccessRoute capabilities={["audiences.source.google_sheets.use"]} module="audiences" permission={{ action: "manage", module: "audiences" }}>
                      <PageAddGoogleSheets />
                    </FeatureAccessRoute>
                  )}
                />
                <Route
                  path="/listagens/adicionar/api"
                  element={(
                    <FeatureAccessRoute capabilities={["audiences.source.external_api.use"]} module="audiences" permission={{ action: "manage", module: "audiences" }}>
                      <PageAddApiRest />
                    </FeatureAccessRoute>
                  )}
                />
                <Route
                  path="/listagens/adicionar/excel"
                  element={(
                    <FeatureAccessRoute capabilities={["audiences.source.excel_file.use"]} module="audiences" permission={{ action: "manage", module: "audiences" }}>
                      <PageAddExcel />
                    </FeatureAccessRoute>
                  )}
                />
                <Route
                  path="/listagens/adicionar/:integration"
                  element={(
                    <ErpAudienceRoute>
                      <AddSheetsIntegrationInvoice />
                    </ErpAudienceRoute>
                  )}
                />
                <Route
                  path="/listagens/adicionar/:integration/faturas em aberto"
                  element={(
                    <ErpAudienceRoute>
                      <AddSheetsIntegrationInvoice />
                    </ErpAudienceRoute>
                  )}
                />

                <Route
                  path="/contatos"
                  element={(
                    <FeatureAccessRoute module="audiences" permission={{ action: "use", module: "audiences" }}>
                      <ContactsPage />
                    </FeatureAccessRoute>
                  )}
                />
                <Route
                  path="/atendimentos/*"
                  element={(
                    <FeatureAccessRoute module="crm.attendance">
                      <AttendancePage />
                    </FeatureAccessRoute>
                  )}
                />

                <Route
                  path={campaignRoutePaths.list}
                  element={(
                    <FeatureAccessRoute module="campaigns" permission={{ action: "view", module: "campaigns" }}>
                      <CampaignPage />
                    </FeatureAccessRoute>
                  )}
                />
                <Route
                  path={campaignRoutePaths.create}
                  element={(
                    <FeatureAccessRoute
                      anyCapabilities={[
                        "campaigns.channel.whatsapp_api.use",
                        "campaigns.channel.sms.use",
                        "campaigns.channel.email.use",
                      ]}
                      module="campaigns"
                      permission={{ action: "create", module: "campaigns" }}
                    >
                      <CampaignCreatePage />
                    </FeatureAccessRoute>
                  )}
                />
                <Route
                  path={campaignRoutePaths.edit}
                  element={(
                    <FeatureAccessRoute module="campaigns" permission={{ action: "edit", module: "campaigns" }}>
                      <CampaignEditPage />
                    </FeatureAccessRoute>
                  )}
                />
                <Route
                  path={campaignRoutePaths.executions}
                  element={(
                    <FeatureAccessRoute module="campaigns" permission={{ action: "reports", module: "campaigns" }}>
                      <CampaignAnalysisPage />
                    </FeatureAccessRoute>
                  )}
                />
                <Route
                  path={campaignRoutePaths.executionAnalysis}
                  element={(
                    <FeatureAccessRoute module="campaigns" permission={{ action: "reports", module: "campaigns" }}>
                      <CampaignAnalysisPage />
                    </FeatureAccessRoute>
                  )}
                />

                <Route path="/integracoes" element={<LegacyIntegrationsRedirect />} />
              </Route>
            </Route>
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Box>
    </Suspense>
  );
}
